import { z } from 'zod';

/**
 * Force probante d'une pièce — hiérarchie juridique FR de la valeur d'un
 * document comme preuve. Ajoutée en M068 sur `case_documents.probative_force`,
 * inférée par l'agent Ingest selon la nature du document.
 *
 * Hiérarchie descendante :
 *   `authentic` (acte notarié, huissier)
 *     > `private_signed` (acte sous seing privé signé)
 *     > `private_initials` (acte SSP avec initiales seulement)
 *     > `attestation` (attestation art. 202 CPC)
 *     > `email`
 *     > `internal_note` (note interne, projet, brouillon)
 *     > `press` (presse, source publique)
 *     > `other`
 *
 * Utilisé pour le scoring d'importance des pièces (Section ④ Pièces clés)
 * et comme signal pour le Reviewer dans l'arbitrage des contradictions.
 */
export const ProbativeForceSchema = z.enum([
  'authentic',
  'private_signed',
  'private_initials',
  'attestation',
  'email',
  'internal_note',
  'press',
  'other',
]);
export type ProbativeForce = z.infer<typeof ProbativeForceSchema>;

/**
 * Métadonnées par valeur de `ProbativeForce` pour l'affichage UI et les
 * tooltips. Le score numérique est utilisé dans la formule d'importance
 * des pièces (cf. case-wiki/data-model.md §7.1).
 */
export const PROBATIVE_FORCE_INFO: Record<
  ProbativeForce,
  { label: string; description: string; score: number }
> = {
  authentic: {
    label: 'Acte authentique',
    description: 'Acte établi par un officier public (notaire, huissier) — force probante la plus haute.',
    score: 5,
  },
  private_signed: {
    label: 'Acte sous seing privé signé',
    description: 'Document écrit signé par les parties — force probante forte.',
    score: 3,
  },
  private_initials: {
    label: 'SSP avec initiales',
    description: 'Acte sous seing privé paraphé sans signature complète — force probante atténuée.',
    score: 2,
  },
  attestation: {
    label: 'Attestation (art. 202 CPC)',
    description: 'Témoignage écrit d\'un tiers, sous serment et avec mention manuscrite — force probante notable.',
    score: 2,
  },
  email: {
    label: 'Email',
    description: 'Correspondance électronique — force probante variable selon le contenu et le contexte.',
    score: 1,
  },
  internal_note: {
    label: 'Note interne',
    description: 'Document interne, projet, brouillon — faible force probante.',
    score: 0,
  },
  press: {
    label: 'Presse / source publique',
    description: 'Article de presse ou source publique — force probante limitée pour les faits litigieux.',
    score: 0,
  },
  other: {
    label: 'Autre',
    description: 'Force probante non classée.',
    score: 0,
  },
};
